import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Check } from 'lucide-react';

const STORAGE_KEY = 'gojowy_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setTimeout(() => setVisible(true), 800);
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent({ necessary: true, ...prefs });

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Main Banner */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            data-testid="cookie-banner"
            className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-[#EDE8E0] shadow-xl"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <p className="font-outfit text-sm text-[#2A2A2A] mb-1">
                    <strong className="font-semibold">Diese Website verwendet Cookies</strong>
                  </p>
                  <p className="font-outfit text-xs text-[#2A2A2A]/60 leading-relaxed">
                    Wir nutzen Cookies für ein optimales Nutzererlebnis. Weitere Informationen finden Sie in unserer{' '}
                    <a href="/datenschutz" className="text-[#8B7355] hover:underline">
                      Datenschutzerklärung
                    </a>.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="bg-[#8B7355] text-white px-5 py-2.5 font-outfit text-xs tracking-wide uppercase hover:bg-[#6B5440] transition-colors duration-200 whitespace-nowrap"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    data-testid="cookie-accept-necessary"
                    onClick={acceptNecessary}
                    className="border border-[#EDE8E0] text-[#2A2A2A] px-5 py-2.5 font-outfit text-xs tracking-wide uppercase hover:border-[#8B7355] hover:text-[#8B7355] transition-colors duration-200 whitespace-nowrap"
                  >
                    Nur notwendige
                  </button>
                  <button
                    data-testid="cookie-settings-btn"
                    onClick={() => setShowSettings(true)}
                    className="flex items-center gap-1.5 text-[#2A2A2A]/55 hover:text-[#8B7355] transition-colors font-outfit text-xs"
                  >
                    <Settings size={13} />
                    Einstellungen
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Settings Modal */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center px-4"
                data-testid="cookie-settings-modal"
              >
                <div className="absolute inset-0 bg-black/50" onClick={() => setShowSettings(false)} />
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="relative bg-white border border-[#EDE8E0] p-8 max-w-md w-full z-10 shadow-2xl"
                >
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="font-cormorant text-xl text-[#2A2A2A] font-semibold">
                      Cookie-Einstellungen
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      data-testid="cookie-settings-close"
                      className="text-[#2A2A2A]/40 hover:text-[#2A2A2A] transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="space-y-5 mb-8">
                    {/* Notwendig */}
                    <div className="flex items-start justify-between gap-4 pb-5 border-b border-[#EDE8E0]">
                      <div>
                        <p className="font-outfit text-sm font-medium text-[#2A2A2A] mb-1">Notwendige Cookies</p>
                        <p className="font-outfit text-xs text-[#2A2A2A]/55 leading-relaxed">
                          Erforderlich für den Betrieb der Website. Können nicht deaktiviert werden.
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-8 h-5 bg-[#8B7355] rounded-full flex items-center justify-end px-0.5">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start justify-between gap-4 pb-5 border-b border-[#EDE8E0]">
                      <div>
                        <p className="font-outfit text-sm font-medium text-[#2A2A2A] mb-1">Analyse-Cookies</p>
                        <p className="font-outfit text-xs text-[#2A2A2A]/55 leading-relaxed">
                          Helfen uns zu verstehen, wie Besucher unsere Website nutzen.
                        </p>
                      </div>
                      <button
                        data-testid="cookie-toggle-analytics"
                        onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                        className={`flex-shrink-0 w-8 h-5 rounded-full flex items-center transition-colors duration-200 px-0.5 ${prefs.analytics ? 'bg-[#8B7355] justify-end' : 'bg-[#EDE8E0] justify-start'}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-outfit text-sm font-medium text-[#2A2A2A] mb-1">Marketing-Cookies</p>
                        <p className="font-outfit text-xs text-[#2A2A2A]/55 leading-relaxed">
                          Ermöglichen personalisierte Werbung und Tracking über Drittanbieter.
                        </p>
                      </div>
                      <button
                        data-testid="cookie-toggle-marketing"
                        onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                        className={`flex-shrink-0 w-8 h-5 rounded-full flex items-center transition-colors duration-200 px-0.5 ${prefs.marketing ? 'bg-[#8B7355] justify-end' : 'bg-[#EDE8E0] justify-start'}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      data-testid="cookie-save-custom"
                      onClick={saveCustom}
                      className="flex-1 bg-[#8B7355] text-white py-3 font-outfit text-xs tracking-wide uppercase hover:bg-[#6B5440] transition-colors flex items-center justify-center gap-2"
                    >
                      <Check size={13} />
                      Speichern
                    </button>
                    <button
                      data-testid="cookie-modal-accept-all"
                      onClick={acceptAll}
                      className="flex-1 border border-[#EDE8E0] text-[#2A2A2A] py-3 font-outfit text-xs tracking-wide uppercase hover:border-[#8B7355] transition-colors"
                    >
                      Alle akzeptieren
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
